import { Link } from "react-router-dom";
import { Sparkles, ChevronLeft } from "lucide-react";

export default function PrivacyPage() {
  return (
    <div className="max-w-3xl mx-auto px-6 py-10 text-slate-300 leading-relaxed" data-testid="privacy-page">
      <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-amber-400 mb-6">
        <ChevronLeft className="h-3.5 w-3.5" /> Back
      </Link>

      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="h-5 w-5 text-amber-400" />
        <h1 className="font-cinzel text-2xl sm:text-3xl font-bold text-amber-400 tracking-wider">Privacy Policy</h1>
      </div>
      <p className="text-xs text-slate-500 mb-8">Last updated: February 2026</p>

      <section className="space-y-5 text-sm">
        <p>
          Tara Kundli ("we", "us", "our") is a Vedic astrology application that generates
          personalised birth charts and readings. We respect your privacy and
          this policy explains what data we collect, why, and how we protect it.
        </p>

        <h2 className="text-base font-semibold text-amber-400 mt-6">1. Information we collect</h2>
        <ul className="list-disc pl-5 space-y-2">
          <li><b>Account data</b>: email address, display name, and password hash (bcrypt; we never see your plaintext password). For Google sign-in, your name, email, and profile picture from Google.</li>
          <li><b>Birth chart data</b>: name, date, time, and place of birth that you enter to generate a Kundli. This is stored only when you explicitly save a chart.</li>
          <li><b>Usage data</b>: which features you use, daily reading counts, and anonymous activity logs (no PII tied to the body of questions you ask).</li>
          <li><b>Payment data</b>: when you purchase a plan, payments are processed by Stripe. We store only the transaction ID, amount, and status — never your card details.</li>
        </ul>

        <h2 className="text-base font-semibold text-amber-400 mt-6">2. How we use your data</h2>
        <ul className="list-disc pl-5 space-y-2">
          <li>To authenticate you and keep you signed in.</li>
          <li>To compute your astrological chart (calculations are done on our servers using Swiss Ephemeris).</li>
          <li>To generate the readings of your chart. Your chart data and questions are sent to OpenAI / Anthropic / Google via our LLM provider; they are not used to train their models.</li>
          <li>To send transactional notifications related to your account or purchases.</li>
        </ul>

        <h2 className="text-base font-semibold text-amber-400 mt-6">3. Data sharing</h2>
        <p>
          We do <b>not</b> sell your data. We share data only with:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li><b>Stripe</b> — payment processing (when you purchase).</li>
          <li><b>OpenAI / Anthropic / Google AI</b> — to generate readings (each request is stateless).</li>
          <li><b>Google Places API</b> — to look up the latitude/longitude/timezone of your birthplace.</li>
          <li><b>Legal authorities</b> — only where required by law.</li>
        </ul>

        <h2 className="text-base font-semibold text-amber-400 mt-6">4. Data retention & deletion</h2>
        <p>
          Your account data is kept while your account is active. You can request deletion at any
          time by emailing <a className="text-amber-400" href="mailto:support@tarakundli.com">support@tarakundli.com</a>;
          we will permanently delete your account, saved charts, and payment history within 30 days.
        </p>

        <h2 className="text-base font-semibold text-amber-400 mt-6">5. Security</h2>
        <p>
          Passwords are hashed with bcrypt. All traffic is encrypted over HTTPS. Session tokens
          expire after 30 days. We do not store payment card details on our servers.
        </p>

        <h2 className="text-base font-semibold text-amber-400 mt-6">6. Children's privacy</h2>
        <p>Tara Kundli is not intended for users under 13. We do not knowingly collect data from children.</p>

        <h2 className="text-base font-semibold text-amber-400 mt-6">7. Notifications (mobile app)</h2>
        <p>
          If you opt in, we may send local notifications (e.g. "Your Card of the Day awaits"). These
          are scheduled on your device — we do not send remote pushes without your consent. You can
          disable them in the OS settings at any time.
        </p>

        <h2 className="text-base font-semibold text-amber-400 mt-6">8. Your rights (GDPR / CCPA)</h2>
        <p>
          You have the right to access, correct, export, or delete your data. Email
          <a className="text-amber-400" href="mailto:support@tarakundli.com"> support@tarakundli.com</a>.
        </p>

        <h2 className="text-base font-semibold text-amber-400 mt-6">9. Changes</h2>
        <p>
          We will post any updates to this policy on this page and update the "Last updated" date.
        </p>

        <h2 className="text-base font-semibold text-amber-400 mt-6">10. Contact</h2>
        <p>
          Questions? Reach us at <a className="text-amber-400" href="mailto:support@tarakundli.com">support@tarakundli.com</a>.
        </p>
      </section>
    </div>
  );
}
